/*
 * transition-rescue.js v2.4
 * Blockshop Transition class timing-race fix -- SCOPED
 *
 * v2.4 (2026-04-30): Custom-element parent exclusion cleanup
 *   Removed shop-the-look-element, carousel-element, and
 *   cart-element from EXCLUDED_CONTEXTS.
 *
 *   Reasoning:
 *     - The trigger-attribute guard (data-transition-trigger) is
 *       the canonical contract. Components that own their items'
 *       activation declare trigger="custom" / "hover" / "click".
 *       Adding their parent custom-element names to a blocklist
 *       was redundant and obscured that contract.
 *     - carousel-element exclusion was actively harmful: this
 *       rescue script was originally created to fix stuck
 *       homepage carousel images during the 10.3 -> 12.2.1
 *       migration. Excluding the entire carousel subtree blocked
 *       the script's founding purpose. Carousel-managed slide
 *       state is already untouched (the slide-block elements
 *       don't carry data-transition-item), but content inside
 *       slides uses regular viewport entrance animations that
 *       legitimately need rescue.
 *     - cart-element exclusion blocked rescue of AJAX-rendered
 *       cart content. The trigger guard already protects items
 *       that the cart-element JS controls.
 *     - shop-the-look-element exclusion was harmless but redundant.
 *       The trigger guard alone catches it (products: custom,
 *       hotspots: hover). aria-hidden guard is a second layer.
 *
 *   Net: trigger guard + aria-hidden guard + escape hatch is the
 *   contract. Parent-context exclusions stay only where the
 *   inner items might legitimately use trigger="viewport" but
 *   should still never be force-activated (modals, menus,
 *   search, filter drawers, etc.).
 *
 * Dionis Goat Milk Skincare -- BH Consulting LLC
 */

(function () {
  'use strict';

  var rescueObserver = null;
  var observeTimer = null;
  var forceTimer = null;
  var resizeTimer = null;

  var OBSERVE_DELAY = 600;
  var FORCE_DELAY = 4000;

  /*
   * Layer 1 of trigger filtering: selector.
   * Match only items the theme's viewport observer is supposed to
   * activate. Items declared with a non-viewport trigger
   * (custom / hover / click) are someone else's responsibility.
   */
  var RESCUE_SELECTOR = [
    '[data-transition-item][data-transition-trigger="viewport"]:not([data-transition-active="true"])',
    '[data-transition-item]:not([data-transition-trigger]):not([data-transition-active="true"])'
  ].join(',');

  /*
   * Parent contexts that must NEVER be touched, even if an inner
   * item happens to use trigger="viewport". These whole subtrees
   * are interactive state managed by the theme or by app integrations.
   *
   * v2.4: shop-the-look-element, carousel-element, cart-element
   * removed. Trigger guard + aria-hidden guard cover those without
   * blocking legitimate rescue cases inside carousel slides and
   * AJAX-rendered cart content.
   */
  var EXCLUDED_CONTEXTS = [
    'modal-element',
    '.modal--root',
    '.x-menu--level-2--wrapper',
    '.x-menu--level-2--list',
    '.y-menu--level-2--container',
    '.y-menu--level-3--container',
    '.predictive-search--products',
    '.search--results',
    '.filter--menu',
    '.disclosure--form',
    '.feedback-bar--root',
    '.main-product--root',
    '.product-media--root',
    '.product-media--thumb',
    'product-zoom-element'
  ].join(', ');

  /*
   * Section types whose [data-transition-item] elements are entirely
   * theme-managed and must never have stage 2 applied.
   */
  var EXCLUDED_SECTION_TYPES = ['product_page', 'header'];

  function isHidden(el) {
    var style = window.getComputedStyle(el);
    return style.visibility === 'hidden' || parseFloat(style.opacity) === 0;
  }

  /*
   * Final gate before activate(). Layer 2 of trigger filtering plus
   * all the v2.1 contextual exclusions plus the v2.3 escape hatch.
   * Anything failing this check is left strictly alone.
   */
  function isSafeToRescue(el, forStage2) {
    if (!el) return false;

    // Already active. Nothing to do.
    if (el.getAttribute('data-transition-active') === 'true') return false;

    /*
     * v2.3 escape hatch. Any element (or any ancestor) marked
     * data-transition-rescue="false" is treated as opt-out.
     * Honored before all other checks so it cannot be bypassed.
     */
    if (el.getAttribute('data-transition-rescue') === 'false') return false;
    if (el.closest('[data-transition-rescue="false"]')) return false;

    /*
     * Trigger gate. Duplicates the selector intentionally so that
     * any future code path which calls activate() directly (rather
     * than going through findStuck) still respects the contract.
     */
    var trigger = el.getAttribute('data-transition-trigger');
    if (trigger && trigger !== 'viewport') return false;

    /*
     * aria-hidden gate. In Blockshop, aria-hidden="true" on a
     * transition item means another component has intentionally
     * hidden it. Entrance animations do not use this attribute.
     */
    if (el.getAttribute('aria-hidden') === 'true') return false;

    // Parent-context exclusions
    if (el.closest(EXCLUDED_CONTEXTS)) return false;

    // Section-type exclusions
    var sectionRoot = el.closest('[data-section-type]');
    if (sectionRoot) {
      var sectionType = sectionRoot.getAttribute('data-section-type');
      if (EXCLUDED_SECTION_TYPES.indexOf(sectionType) !== -1) return false;
    }

    /*
     * Stage 2 only: mobile menu items without a clear section type.
     * Stage 1 (viewport-driven) cannot incorrectly activate these
     * because they are off-viewport when collapsed, but stage 2
     * (force-activate) would.
     */
    if (forStage2) {
      if (
        el.closest('.y-menu--level-1--container') ||
        el.closest('.y-menu--level-1--link') ||
        el.closest('y-menu-element')
      ) return false;
    }

    return true;
  }

  function findStuck(forStage2) {
    var items = document.querySelectorAll(RESCUE_SELECTOR);
    var stuck = [];
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (!isHidden(el)) continue;
      if (!isSafeToRescue(el, forStage2)) continue;
      stuck.push(el);
    }
    return stuck;
  }

  function activate(el) {
    /*
     * Layer 3 of defense. If a future caller routes around findStuck
     * and calls activate() directly, this final guard still holds.
     * forStage2=false is the safer default here (more permissive
     * than stage 2's extra mobile-menu exclusions).
     */
    if (!isSafeToRescue(el, false)) return;

    el.setAttribute('data-transition-active', 'true');

    var finished = false;
    function markFinished() {
      if (finished) return;
      finished = true;
      el.setAttribute('data-transition-finished', 'true');
    }
    el.addEventListener('transitionend', markFinished, { once: true });
    setTimeout(markFinished, 1200);
  }

  function stage1() {
    if (rescueObserver) {
      rescueObserver.disconnect();
      rescueObserver = null;
    }

    var stuck = findStuck(false);
    if (!stuck.length) return;

    rescueObserver = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          var target = entries[i].target;
          activate(target);
          if (rescueObserver) rescueObserver.unobserve(target);
        }
      }
    }, { threshold: 0.01 });

    for (var i = 0; i < stuck.length; i++) {
      rescueObserver.observe(stuck[i]);
    }
  }

  function stage2() {
    var stuck = findStuck(true);
    for (var i = 0; i < stuck.length; i++) {
      activate(stuck[i]);
    }
  }

  function rescue() {
    clearTimeout(observeTimer);
    clearTimeout(forceTimer);
    observeTimer = setTimeout(stage1, OBSERVE_DELAY);
    forceTimer = setTimeout(stage2, FORCE_DELAY);
  }

  // --- Trigger points ---

  if (document.readyState === 'complete') {
    rescue();
  } else {
    window.addEventListener('load', rescue);
  }

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(rescue, 400);
  });

  window.addEventListener('orientationchange', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(rescue, 200);
  });

  document.addEventListener('shopify:section:load', function () {
    setTimeout(rescue, 300);
  });

  document.addEventListener('shopify:section:select', function () {
    setTimeout(rescue, 100);
  });

})();